import {
  PAYMENT_DIALOG_CONTENT,
  PaymentStatus,
} from "../../../constants/payment";
import {
  TypographyColor,
  TypographyVariant,
} from "../../../constants/typography";
import React from "react";
import { useTranslation } from "react-i18next";
import { Typography } from "../../core/Typography/Typography";
import { Dialog } from "../../core/Dialog/Dialog";
import { useStyles } from "./PaymentDialog.style";

interface PaymentDialogProps {
  status: PaymentStatus;
  open: boolean;
  onClose: () => void;
  onTryAgain?: () => void;
  onStartPlaying?: () => void;
}

export const PaymentDialog: React.FC<PaymentDialogProps> = ({
  status,
  open,
  onClose,
  onTryAgain,
  onStartPlaying,
}) => {
  const { t } = useTranslation();
  const dialogContent = PAYMENT_DIALOG_CONTENT[status];
  const classes = useStyles();

  const mainActionOnClick = (() => {
    switch (status) {
      case PaymentStatus.SUCCESS:
        return onStartPlaying ?? onClose;
      case PaymentStatus.CANCELLED:
      case PaymentStatus.FAILED:
        return onTryAgain ?? onClose;
      case PaymentStatus.PENDING:
        return onClose;
      default:
        return onClose;
    }
  })();

  const getMainAction = () => {
    const action = dialogContent.mainAction;
    if (!action) return undefined;

    return {
      text: t(action.textKey),
      onClick: mainActionOnClick,
      id: action.id,
      "data-gamepad": "default-selected",
    };
  };

  const getSecondAction = () => {
    const action = dialogContent.secondAction;
    if (!action) return undefined;

    return {
      text: t(action.textKey),
      onClick: onClose,
      id: action.id,
    };
  };

  return (
    <Dialog
      title={t(dialogContent.headerKey)}
      onClose={dialogContent.secondAction ? onClose : mainActionOnClick}
      mainAction={getMainAction()}
      secondAction={getSecondAction()}
      open={open}
      className={classes.dialogContent}
    >
      <Typography
        variant={TypographyVariant.HeadingH3Bold}
        className={classes.header}
      >
        {t(dialogContent.headerKey)}
      </Typography>

      <div className={classes.content}>
        {dialogContent.contentKeys.map((paragraph, index) => (
          <Typography
            key={index}
            variant={TypographyVariant.BodyB3Medium}
            color={TypographyColor.Default}
            className={classes.contentText}
          >
            {t(paragraph)}
          </Typography>
        ))}
      </div>
    </Dialog>
  );
};
